import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { Module } from '@pc/core';
import { appBus } from '@pc/core';
import {
  listMyMemberships,
  listMyOrganizations,
  listStoresForOrg,
  membershipIsOrgWide,
  stampBrowserRequestContext,
} from '@pc/db';
import { ShopMessageNotificationWatcher } from '@pc/shop-ui';
import { CalendarPublicActionWatcher } from '@pc/modules-calendar';
import {
  Button,
  Drawer,
  IdleLock,
  Modal,
  ReadOnlyBanner,
  Rule,
  ToastProvider,
  bindToastsToBus,
  useIsMobile,
  useToast,
} from '@pc/ui';
import { useRouteTracking } from '@pc/analytics-client';
import { FooterLanguagePicker, useApplyAccountLocale } from '@pc/i18n';
import { useAuth } from '../auth/AuthProvider';
import { useActiveContext } from '../auth/ActiveContextProvider';
import { usePlatformAdmin } from '../admin/usePlatformAdmin';
import { env } from '../env';
import { DevSchemaParityBanner } from './devSchemaParityCheck';
import { DevModeBanner } from '../devmode/DevModeBanner';
import { DevModeDock } from '../devmode/DevModeDock';
import { DevModeSidebarEntry } from '../devmode/DevModeSidebarEntry';
import {
  operatorShellAsideClass,
  operatorShellFrameClass,
  operatorShellGridClass,
  operatorShellMainClass,
  operatorShellMainColumnClass,
  operatorShellNavClass,
} from './operatorShellLayout';
import { TrialBanner } from './TrialBanner';
import { setReporterActiveContext } from './getActiveContextForReporter';
import {
  MobileTabBar,
  HomeIcon,
  MenuIcon,
  iconForRoute,
  type MobileTab,
} from './MobileTabBar';
import { GlobalSearch } from './GlobalSearch';
import { NavRow, useNavPins } from './NavPins';

// Daily-driver routes, in the order we'd surface them in the phone bottom
// bar. We take the first three that the active org actually has enabled,
// then backfill from the remaining nav items so the bar is never sparse.
const MOBILE_TAB_PRIORITY = [
  '/clients',
  '/orders',
  '/calendar',
  '/products',
  '/fulfillment',
  '/wholesalers',
  '/reports',
];

interface AppShellProps {
  modules: ReadonlyArray<Module>;
}

export function AppShell({ modules }: AppShellProps) {
  return (
    <ToastProvider>
      <div className={operatorShellFrameClass}>
        <DevSchemaParityBanner modules={modules} />
        <ShellInner modules={modules} />
      </div>
    </ToastProvider>
  );
}

function ShellInner({ modules }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, signOut } = useAuth();
  const { activeOrgId, activeStoreId, setActiveStore, clear } = useActiveContext();
  const toast = useToast();
  const adminQ = usePlatformAdmin();
  const [navOpen, setNavOpen] = useState(false);
  const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);
  const [accessWarningOpen, setAccessWarningOpen] = useState(false);
  const [pendingStoreId, setPendingStoreId] = useState<string | null>(null);
  const [storeSearch, setStoreSearch] = useState('');
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // Apply the account-level language preference (core.users.preferred_locale)
  // once the session is live, so the user's language follows them to any
  // device — not just the browser whose localStorage they last set.
  useApplyAccountLocale(Boolean(session));

  const orgsQ = useQuery({
    queryKey: ['shell', 'my-orgs'],
    queryFn: listMyOrganizations,
    staleTime: 5 * 60_000,
    enabled: Boolean(activeOrgId),
  });
  const storesQ = useQuery({
    queryKey: ['shell', 'stores-for-org', activeOrgId],
    queryFn: () => listStoresForOrg(activeOrgId as string),
    staleTime: 5 * 60_000,
    enabled: Boolean(activeOrgId),
  });
  // Defense-in-depth for the workspace switcher: RLS on core.stores is the
  // authoritative gate, but we also filter the list against the user's own
  // membership grants so a future RLS regression can't silently expose stores
  // the operator was scoped out of. Mirrors OrgStorePicker's `visibleStores`.
  const membershipsQ = useQuery({
    queryKey: ['shell', 'my-memberships'],
    queryFn: listMyMemberships,
    staleTime: 5 * 60_000,
    enabled: Boolean(activeOrgId),
  });

  // Self-hosted analytics: one POST per route change for the
  // authenticated operator. Paused until the session resolves so we
  // don't double-fire the initial paint (the AuthProvider re-renders
  // once after the session hydrates).
  useRouteTracking({ paused: !session });

  // Any route change auto-closes the mobile drawer so the user isn't left
  // staring at the menu after tapping a link. Fires on every pathname
  // transition rather than per-NavLink so non-NavLink anchors (e.g. the
  // admin-console href, the workspace switch button) also benefit.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Resize from phone -> desktop with the drawer left open: close it so
  // the body-scroll-lock and the (now invisible) backdrop don't trap the
  // user. The Drawer's own keyboard handlers can't fire if the user
  // isn't focused inside.
  useEffect(() => {
    if (!isMobile) setNavOpen(false);
  }, [isMobile]);

  // Stamp the HIPAA audit request context (sessionId, requestId, route,
  // user-agent) on every navigation so any mutating RPC fired by the
  // subsequent page render is tagged. Implementation lives in
  // packages/db/src/services/requestContext.ts.
  useEffect(() => {
    void stampBrowserRequestContext(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  // Push the active identity into the singleton the global error
  // reporter reads from. This is best-effort -- a missed update window
  // just means an error is reported with slightly stale context.
  useEffect(() => {
    setReporterActiveContext({
      userId: session?.user.id ?? null,
      userEmail: session?.user.email ?? null,
      orgId: activeOrgId ?? null,
      storeId: activeStoreId ?? null,
    });
  }, [session?.user.id, session?.user.email, activeOrgId, activeStoreId]);

  // Wire module event subscribers into the in-app bus exactly once.
  useEffect(() => {
    const bus = appBus();
    const offs: Array<() => void> = [];
    for (const m of modules) {
      const subs = m.events?.subscribesTo;
      if (!subs) continue;
      for (const key of Object.keys(subs) as Array<keyof typeof subs>) {
        const handler = subs[key];
        if (handler) offs.push(bus.on(key as never, handler as never));
      }
    }
    // Pipe 'app.toast' events to the actual toast renderer.
    offs.push(bindToastsToBus(toast.push, (evt, h) => bus.on(evt as never, h as never)));
    return () => {
      for (const off of offs) off();
    };
  }, [modules, toast]);

  const navItems = useMemo(
    () =>
      modules
        .flatMap((m) => m.navItems.map((n) => ({ ...n, moduleId: m.id })))
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100)),
    [modules],
  );

  // Module navItems carry hardcoded English labels (each module.tsx is a
  // plain object literal — no hook context), so the shell translates them
  // at render time: the English label slugs to a catalog key under
  // app.navLabels.*, falling back to the raw label for any module the
  // catalog doesn't know yet. This keeps new modules pinnable/renderable
  // with zero i18n wiring while every known label localizes.
  const navLabel = (raw: string) =>
    t(`app.navLabels.${raw.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`, raw);

  // Per-user pinned shortcuts (core.user_nav_pins; follows the user across
  // devices). The label map re-resolves a pin's current ENGLISH label from
  // the live nav (so renames propagate), then `navLabel` localizes at
  // render. The stored snapshot is the fallback for pages not in the
  // current module set.
  const { pins, isPinned, toggle } = useNavPins(Boolean(session));
  const pinLabelByPath = useMemo(() => {
    const m = new Map<string, string>([
      ['/', 'Dashboard'],
      ['/settings', 'Settings'],
    ]);
    for (const n of navItems) m.set(n.to, n.label);
    return m;
  }, [navItems]);

  const mobileTabs = useMemo<MobileTab[]>(() => {
    const byTo = new Map(navItems.map((n) => [n.to, n]));
    const picked: typeof navItems = [];
    for (const to of MOBILE_TAB_PRIORITY) {
      const item = byTo.get(to);
      if (item && !picked.includes(item)) picked.push(item);
      if (picked.length >= 3) break;
    }
    for (const item of navItems) {
      if (picked.length >= 3) break;
      if (!picked.includes(item)) picked.push(item);
    }
    return [
      { key: 'home', to: '/', end: true, label: t('app.mobileNav.home'), icon: HomeIcon },
      ...picked.map((n) => ({
        key: n.to,
        to: n.to,
        label: t(`app.navLabels.${n.label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`, n.label),
        icon: iconForRoute(n.to),
      })),
      { key: 'menu', label: t('app.mobileNav.menu'), icon: MenuIcon, onClick: () => setNavOpen(true) },
    ];
  }, [navItems, t]);

  const hasShop = modules.some((m) => m.id === 'shop');
  const hasCalendar = modules.some((m) => m.id === 'calendar');
  const activeOrg = useMemo(
    () => orgsQ.data?.find((o) => o.id === activeOrgId) ?? null,
    [orgsQ.data, activeOrgId],
  );
  const activeStore = useMemo(
    () => storesQ.data?.find((s) => s.id === activeStoreId) ?? null,
    [storesQ.data, activeStoreId],
  );
  // Stores the active membership actually grants: org-wide members see all,
  // store-scoped members only their granted stores. Computed from the user's
  // own (non-archived) rows — independent of what RLS returns.
  const membershipScopedStores = useMemo(() => {
    const stores = storesQ.data ?? [];
    const myRows = (membershipsQ.data ?? []).filter(
      (m) => m.org_id === activeOrgId && !m.archived_at,
    );
    // While memberships are still loading, fall back to the RLS-scoped list
    // rather than flashing an empty switcher.
    if (myRows.length === 0) return stores;
    if (myRows.some((m) => membershipIsOrgWide(m))) return stores;
    const granted = new Set(myRows.map((m) => m.store_id).filter(Boolean));
    return stores.filter((s) => granted.has(s.id));
  }, [storesQ.data, membershipsQ.data, activeOrgId]);

  const filteredStores = useMemo(() => {
    const needle = storeSearch.trim().toLowerCase();
    if (!needle) return membershipScopedStores;
    return membershipScopedStores.filter((s) => {
      const name = s.name.toLowerCase();
      const slug = s.slug.toLowerCase();
      return name.includes(needle) || slug.includes(needle);
    });
  }, [membershipScopedStores, storeSearch]);

  // The sidebar JSX is consumed twice: once by the desktop <aside>
  // (visible at md+) and once by the mobile <Drawer> (visible below
  // md). Extracting it as a const guarantees the two surfaces never
  // drift -- nav parity matters because the operator builds muscle
  // memory on desktop and we don't want phone users to miss links.
  // Show the real org/store NAMES (never the UUID). `activeOrg`/`activeStore`
  // resolve from the user's orgs + the active org's stores; while those queries
  // are in flight we show an em-dash, not the database id.
  const orgLabel = activeOrg?.name ?? null;
  const storeLabel = activeStore?.name ?? null;

  function openWorkspaceModal() {
    setStoreSearch('');
    setWorkspaceModalOpen(true);
  }

  function closeWorkspaceModal() {
    setWorkspaceModalOpen(false);
  }

  function isLikelyRecordDetailPath(pathname: string): boolean {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return false;
    const first = segments[0];
    const second = segments[1];
    if (!second) return false;
    // Known non-record second segments should not be treated as a scoped detail id.
    const nonRecordSecondSegments = new Set([
      'new',
      'settings',
      'inbox',
      'feedback',
      'history',
      'import',
      'developer',
      'usage',
      'billing',
      'account',
      'security',
      'team',
      'templates',
      'store',
      'integrations',
      'webhooks',
      'api-keys',
      'connectors',
      'messaging',
      'profile',
      'pair',
      'dashboard',
    ]);
    if (first === 'settings' || nonRecordSecondSegments.has(second)) return false;
    // UUID-ish identifiers (and most generated ids) are store-scoped records.
    const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidLike.test(second)) return true;
    // Most detail routes are at least 3 segments when nested; treat as scoped.
    return segments.length >= 3;
  }

  function shouldWarnBeforeStoreSwap(pathname: string): boolean {
    return isLikelyRecordDetailPath(pathname);
  }

  function commitStoreSwap(storeId: string, opts?: { goDashboardAfterSwap?: boolean }) {
    if (storeId !== activeStoreId) setActiveStore(storeId);
    if (opts?.goDashboardAfterSwap) navigate('/', { replace: true });
    setPendingStoreId(null);
    setAccessWarningOpen(false);
    closeWorkspaceModal();
  }

  function cancelStoreSwap() {
    setPendingStoreId(null);
    setAccessWarningOpen(false);
    setWorkspaceModalOpen(true);
  }

  function chooseStore(storeId: string) {
    if (storeId === activeStoreId) {
      closeWorkspaceModal();
      return;
    }
    if (shouldWarnBeforeStoreSwap(location.pathname)) {
      setPendingStoreId(storeId);
      setWorkspaceModalOpen(false);
      setAccessWarningOpen(true);
      return;
    }
    commitStoreSwap(storeId);
  }

  function goToWorkspacePicker() {
    closeWorkspaceModal();
    clear();
    navigate('/choose-context');
  }

  const sidebarContent: ReactNode = (
    <>
      <div className="shrink-0 px-5 pt-6 pb-4">
        <a
          href="/"
          className="block mb-3 leading-none"
          aria-label={t('app.shell.homeAriaLabel')}
        >
          <img
            src="https://bxbdqmtldkhgwpnwecox.supabase.co/storage/v1/object/public/Public%20Photos/LOGOUpdated.png"
            alt="PeptideClients"
            width="128"
            height="32"
            className="h-8 w-auto block"
          />
        </a>
      </div>

      <nav className={operatorShellNavClass}>
        {pins.length > 0 ? (
          <>
            <p className="smallcap mb-1">{t('app.shell.pinned')}</p>
            {pins.map((p) => (
              <NavRow
                key={`pin:${p.to_path}`}
                to={p.to_path}
                end={p.to_path === '/'}
                label={navLabel(pinLabelByPath.get(p.to_path) ?? p.label)}
                pinned
                onTogglePin={() =>
                  toggle(p.to_path, pinLabelByPath.get(p.to_path) ?? p.label, true)
                }
              />
            ))}
            <Rule className="my-2" />
          </>
        ) : null}
        <NavRow
          to="/"
          end
          label={navLabel('Dashboard')}
          pinned={isPinned('/')}
          onTogglePin={() => toggle('/', 'Dashboard', isPinned('/'))}
        />
        <Rule className="my-2" />
        {navItems.map((n) => (
          <NavRow
            key={`${n.moduleId}:${n.to}`}
            to={n.to}
            label={navLabel(n.label)}
            pinned={isPinned(n.to)}
            onTogglePin={() => toggle(n.to, n.label, isPinned(n.to))}
          />
        ))}
        <Rule className="my-2" />
        <NavRow
          to="/settings"
          label={navLabel('Settings')}
          pinned={isPinned('/settings')}
          onTogglePin={() => toggle('/settings', 'Settings', isPinned('/settings'))}
        />
        {adminQ.data ? (
          <>
            <Rule className="my-2" />
            <p className="smallcap mb-1">{t('app.shell.platformAdmin')}</p>
            <DevModeSidebarEntry onNavigate={() => setNavOpen(false)} />
            {env.adminConsoleUrl ? (
              <a
                href={env.adminConsoleUrl}
                target="_blank"
                rel="noreferrer"
                className="py-2 text-sm text-muted hover:text-ink"
              >
                {t('app.shell.adminConsole')} <span aria-hidden>↗</span>
              </a>
            ) : null}
          </>
        ) : null}
      </nav>

      <div className="shrink-0 px-5 py-4 border-t border-rule flex flex-col gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm text-ink" title={storeLabel ?? undefined}>
            {storeLabel ?? '—'}
          </p>
          <p className="truncate text-xs text-muted" title={orgLabel ?? undefined}>
            {orgLabel ?? '—'}
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={openWorkspaceModal}>
          {t('app.shell.switchWorkspace')}
        </Button>
        <Button size="sm" variant="subtle" onClick={signOut}>
          {t('actions.signOut')}
        </Button>
      </div>
    </>
  );

  return (
    // Scroll contract lives in `operatorShellLayout.ts` — frame locked to
    // `100dvh`, sidebar rail uses `h-full`, main is the only scroll surface.
    <div className={operatorShellGridClass}>
      {/* Desktop sidebar (>=md). Sized to the shell frame (`h-full`, not
          `100dvh`) so a dev banner or mobile chrome cannot re-expand the
          grid row. Wheel events must not bubble into <main>. Nav alone
          scrolls when links overflow. */}
      <aside
        className={operatorShellAsideClass}
        onWheel={(e) => e.stopPropagation()}
      >
        {sidebarContent}
      </aside>

      {/* Mobile topbar (<md). Sticky, paper-tone, hamburger + sign-out. */}
      <header className="md:hidden sticky top-0 z-30 bg-paper/90 backdrop-blur border-b border-rule">
        <div className="flex items-center justify-between gap-3 px-4 h-14 pt-[env(safe-area-inset-top)]">
          <button
            type="button"
            aria-label={t('app.shell.openNavigation')}
            aria-expanded={navOpen}
            aria-controls="app-mobile-nav"
            onClick={() => setNavOpen(true)}
            className="-ml-2 p-2 text-ink"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true" className="w-6 h-6">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <div className="flex flex-1 min-w-0 justify-end">
            <GlobalSearch enableShortcut={false} />
          </div>
          <Button size="sm" variant="ghost" onClick={signOut}>
            {t('actions.signOut')}
          </Button>
        </div>
      </header>

      {/* Mobile nav drawer (<md). Same sidebarContent so nav parity is
          guaranteed. Drawer slides in from the left to mirror the
          desktop sidebar position. */}
      <Drawer
        open={navOpen}
        onClose={() => setNavOpen(false)}
        side="left"
        ariaLabel={t('app.shell.navAriaLabel')}
        widthClassName="w-[18rem] md:hidden"
      >
        {sidebarContent}
      </Drawer>

      <div className={operatorShellMainColumnClass}>
        <main className={operatorShellMainClass}>
          {hasShop && activeOrgId ? (
            <ShopMessageNotificationWatcher
              side="buyer"
              buyerOrgId={activeOrgId}
              inboxBasePath="/shop/inbox"
            />
          ) : null}
          {hasCalendar ? (
            <CalendarPublicActionWatcher orgId={activeOrgId ?? null} />
          ) : null}
          <TrialBanner />
          {/*
            Read-only banner shown to members whose role is `viewer`.
            DB-level RLS still denies any INSERT / UPDATE / DELETE these
            members might attempt via direct API calls; the banner +
            the per-module `useCanWriteOrg()` UI gating is the UX layer
            on top. See migration
            `20260702060000_role_writer_gate.sql` and `@pc/ui/useMyOrgRole`.
          */}
          <ReadOnlyBanner />
          <IdleLock appKey="web" onSignOut={signOut} />
          <DevModeBanner />
          <DevModeDock />
          <div className="sticky top-0 z-30 hidden justify-end border-b border-rule bg-paper/95 px-4 py-2 backdrop-blur md:flex md:px-10">
            <GlobalSearch />
          </div>
          <div
            className={`flex flex-col px-4 py-6 md:px-10 md:py-10 ${
              // The shipment board is a horizontally-scrolling kanban: the
              // 1200px reading-width cap would clip its columns mid-page on
              // wide front-office displays, so it alone goes full-width.
              location.pathname.startsWith('/fulfillment/board')
                ? ''
                : 'md:max-w-[1200px]'
            }`}
          >
            {/*
              Remount the entire routed subtree whenever the active org/store
              changes. Modules read context from localStorage via a NON-reactive
              `useActiveCtx()`, and the data router memoizes matched route
              elements — so switching store from a list/dashboard page (no
              navigation) otherwise leaves stale data on screen until a manual
              refresh. Keying the Outlet forces a fresh render that re-reads the
              context and refetches.
            */}
            <Outlet key={`${activeOrgId ?? ''}:${activeStoreId ?? ''}`} />
            {/*
              Operator app footer — last item in the scroll, directly
              after route content (e.g. SettingsLayout's role summary).
              Cross-origin legal links; do NOT swap for react-router
              <Link>. Not sticky; only visible after scrolling to the
              end of the page.
            */}
            <footer className="shrink-0 border-t border-rule mt-12 pt-6 text-xs text-muted pb-[env(safe-area-inset-bottom)]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p>{t('app.footer.copyright')}</p>
                <p>
                  <a href="https://docs.peptideclients.com/terms" className="hover:underline">
                    {t('app.footer.terms')}
                  </a>{' '}
                  ·{' '}
                  <a href="https://docs.peptideclients.com/privacy" className="hover:underline">
                    {t('app.footer.privacy')}
                  </a>{' '}
                  ·{' '}
                  <a
                    href="https://docs.peptideclients.com/research-use-only"
                    className="hover:underline"
                  >
                    {t('app.footer.researchUseOnly')}
                  </a>
                </p>
                <FooterLanguagePicker />
              </div>
            </footer>
          </div>
        </main>

        {/* App-like bottom navigation (phones only). A flex sibling of
            <main>, so the scroll surface shrinks and page content is never
            clipped. Hidden at >=md where the sidebar rail takes over. */}
        <MobileTabBar tabs={mobileTabs} ariaLabel={t('app.mobileNav.label')} />
      </div>

      <Modal
        open={workspaceModalOpen}
        onClose={closeWorkspaceModal}
        title={t('app.shell.switchWorkspace')}
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-2">
            {t('app.shell.orgStores', {
              org: activeOrg?.name ?? t('app.shell.currentOrganization'),
            })}
          </p>
          <input
            type="search"
            value={storeSearch}
            onChange={(e) => setStoreSearch(e.target.value)}
            placeholder={t('app.shell.searchStores')}
            className="w-full h-9 border border-rule bg-paper px-3 text-sm text-ink outline-none focus:ring-1 focus:ring-ink/20"
          />
          <div className="border border-rule max-h-72 overflow-y-auto">
            {storesQ.isPending ? (
              <p className="px-3 py-3 text-sm text-muted">{t('app.shell.loadingStores')}</p>
            ) : filteredStores.length > 0 ? (
              <ul>
                {filteredStores.map((store) => {
                  const selected = store.id === activeStoreId;
                  return (
                    <li key={store.id}>
                      <button
                        type="button"
                        onClick={() => chooseStore(store.id)}
                        className={`w-full text-left px-3 py-2 border-b last:border-b-0 border-rule text-sm ${selected ? 'bg-paper-2 text-ink' : 'text-ink-2 hover:bg-paper-2'}`}
                      >
                        <span className="block truncate">{store.name}</span>
                        <span className="block text-[11px] text-muted">/{store.slug}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="px-3 py-3 text-sm text-muted">{t('app.shell.noStoresFound')}</p>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <Button variant="ghost" onClick={goToWorkspacePicker}>
              {t('app.shell.chooseOrganization')}
            </Button>
            <Button variant="ghost" onClick={closeWorkspaceModal}>
              {t('actions.cancel')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={accessWarningOpen}
        onClose={cancelStoreSwap}
        title={t('app.shell.wrongStoreAccess')}
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-2">{t('app.shell.wrongStoreBody')}</p>
          {pendingStoreId ? (
            <p className="text-xs text-muted">
              {t('app.shell.targetStore')}{' '}
              {(storesQ.data ?? []).find((store) => store.id === pendingStoreId)?.name ??
                pendingStoreId}
            </p>
          ) : null}
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={cancelStoreSwap}>
              {t('app.shell.returnToAccess')}
            </Button>
            <Button
              onClick={() => {
                if (!pendingStoreId) return;
                commitStoreSwap(pendingStoreId, {
                  goDashboardAfterSwap: isLikelyRecordDetailPath(location.pathname),
                });
              }}
            >
              {t('app.shell.swapStores')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
